import React, { useState } from "react";
import { MapPin, Clock, Phone, Mail, MessageCircle, Send, User, Stethoscope } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import FAQSection from "../components/FAQSection";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { clinic, homeFaqs } from "../data/mock";
import { getWhatsAppLink } from "../lib/whatsapp";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", concern: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Please fill required fields", description: "Name and phone number are needed to book.", variant: "destructive" });
      return;
    }
    const msg = `Hello Dr. Indresh Dixit,%0A%0AI would like to book a consultation.%0A%0AName: ${form.name}%0APhone: ${form.phone}%0AConcern: ${form.concern || "General liver check-up"}`;
    window.open(`https://wa.me/${clinic.whatsapp}?text=${msg}`, "_blank");
    toast({ title: "Redirecting to WhatsApp", description: "Complete your booking in the WhatsApp chat." });
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <SEO path="/contact" faqs={homeFaqs.slice(0, 5)} />
      <PageHero breadcrumb="Contact"
        title="Book an Appointment"
        subtitle={`Get in touch with ${clinic.doctorName} — ${clinic.tagline}. We're here to help you take charge of your liver health.`} />

      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12">
          {/* FORM */}
          <div>
            <SectionHeading center={false} eyebrow="Request Appointment" title="Send Us Your Details"
              subtitle="Fill the form and we'll connect with you on WhatsApp to confirm your slot." />
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm space-y-5">
              <div>
                <Label htmlFor="name" className="mb-2 block text-slate-700">Full Name <span className="text-red-500">*</span></Label>
                <Input id="name" value={form.name} onChange={update("name")} placeholder="Enter your name" className="h-12" />
              </div>
              <div>
                <Label htmlFor="phone" className="mb-2 block text-slate-700">Phone Number <span className="text-red-500">*</span></Label>
                <Input id="phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="+91 XXXXX XXXXX" className="h-12" />
              </div>
              <div>
                <Label htmlFor="concern" className="mb-2 block text-slate-700">Your Concern</Label>
                <Textarea id="concern" value={form.concern} onChange={update("concern")} placeholder="Briefly describe your symptoms or concern (optional)" rows={4} />
              </div>
              <Button type="submit" size="lg" className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full h-12 text-base font-semibold shadow-lg shadow-sky-500/30">
                <Send size={18} className="mr-2" /> Book via WhatsApp
              </Button>
              <p className="text-xs text-slate-400 text-center">Your details are only used to confirm your appointment.</p>
            </form>
          </div>

          {/* INFO */}
          <div className="space-y-6">
            <SectionHeading center={false} eyebrow="Clinic Information" title="Visit Our Clinics" />
            {clinic.branches.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-sky-500 text-white grid place-items-center shrink-0"><MapPin size={22} /></div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-slate-900">{b.name}</h3>
                    <p className="text-slate-500 text-sm mt-1">{b.address}</p>
                    <p className="flex items-center gap-2 text-sky-600 text-sm font-medium mt-2"><Clock size={15} /> {b.timings}</p>
                    <a href={b.mapUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sky-600 hover:text-sky-700 text-sm font-semibold mt-3">
                      <MapPin size={15} /> Get Directions
                    </a>
                  </div>
                </div>
              </div>
            ))}
            <div className="grid sm:grid-cols-2 gap-4">
              <a href={`tel:${clinic.phone.replace(/\s/g, "")}`} className="bg-sky-50 rounded-2xl p-6 border border-sky-100 hover:shadow-md transition-all">
                <Phone size={22} className="text-sky-600 mb-3" />
                <div className="font-heading font-semibold text-slate-900 text-sm">Call Us</div>
                <div className="text-slate-600 text-sm mt-1">{clinic.phone}</div>
              </a>
              <a href={`mailto:${clinic.email}`} className="bg-sky-50 rounded-2xl p-6 border border-sky-100 hover:shadow-md transition-all">
                <Mail size={22} className="text-sky-600 mb-3" />
                <div className="font-heading font-semibold text-slate-900 text-sm">Email Us</div>
                <div className="text-slate-600 text-sm mt-1 break-all">{clinic.email}</div>
              </a>
            </div>
            <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="block">
              <div className="bg-green-500 hover:bg-green-600 transition-colors rounded-2xl p-6 text-white flex items-center gap-4">
                <MessageCircle size={28} />
                <div>
                  <div className="font-heading font-semibold">Chat on WhatsApp</div>
                  <div className="text-green-50 text-sm">Instant response — {clinic.phone}</div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="pb-16 sm:pb-20">
        <div className="container-app">
          <SectionHeading eyebrow="Find Us" title="Our Clinic Locations on Map"
            subtitle="Visit us at either of our two convenient locations in Varanasi." />
          <div className="grid md:grid-cols-2 gap-6">
            {clinic.branches.map((b, i) => (
              <div key={i} className="rounded-3xl overflow-hidden border border-slate-100 shadow-sm bg-white">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <MapPin size={18} className="text-sky-500" />
                    <span className="font-heading font-semibold text-slate-900">{b.name}</span>
                  </div>
                  <a href={b.mapUrl} target="_blank" rel="noopener noreferrer" className="text-sky-600 text-sm font-semibold hover:text-sky-700">Directions</a>
                </div>
                <iframe
                  title={`Map — ${b.name}`}
                  src={b.embedUrl}
                  width="100%" height="340" style={{ border: 0 }} allowFullScreen loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection faqs={homeFaqs.slice(0, 5)} title="Appointment FAQs" />
    </div>
  );
};

export default Contact;
